export enum UserRole {
  admin = 'admin',
  influencer = 'influencer',
}

export const SOCIAL_PLATFORMS = [
  'twitter',
  'discord',
  'youtube',
  'twitch',
  'instagram',
  'tiktok',
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export interface UserData {
  username: string;
  usernameNumber: number;
  email: string;
  id: string;
  roles: UserRole[];
  avatar?: string;
  banner?: string | null;
  bio?: string | null;
  socialLinks?: Partial<Record<SocialPlatform, string>> | null;
  strategies?: string[];
  private: boolean;
  phone?: string;
  affiliationCode: string;
  /// Code lisible du lien d'affiliation, quand l'accès a été donné.
  affiliationAlias?: string | null;
  onboardingCompleted: boolean;
  /// Vrai quand le compte a un mot de passe : un compte créé par un
  /// fournisseur tiers n'en a pas, et l'écran de suppression ne doit pas en
  /// réclamer un. Optionnel, les réponses antérieures ne le portent pas.
  hasPassword?: boolean;
  /// Compte de démonstration du site vitrine — calculé par le serveur et lu par
  /// `useDemoLock`. Optionnel : les réponses antérieures ne le portent pas.
  isDemo?: boolean;
  /// Date d'inscription (ISO) : l'offre d'essai et la mesure du temps par
  /// page ne visent que les comptes récents. Optionnelle comme `isDemo`.
  createdAt?: string;
  /// Réponse à « Comment nous as-tu connu ? » : nulle tant que le compte n'a
  /// pas répondu ; absente d'un serveur qui ne la connaît pas encore.
  heardFrom?: HeardFrom | null;
}

/// Réponses à « Comment nous as-tu connu ? », dans l'ordre affiché.
export const HEARD_FROM = [
  'youtube',
  'tiktok',
  'x',
  'instagram',
  'google',
  'influencer',
  'other',
] as const;
export type HeardFrom = (typeof HEARD_FROM)[number];

export const TRADING_STRATEGIES = [
  'SMC_ICT',
  'ORDERFLOW',
  'INDICATEURS',
  'EA_BOTS',
  'CHARTISME',
] as const;
export type TradingStrategy = (typeof TRADING_STRATEGIES)[number];
export const STRATEGY_LABELS: Record<TradingStrategy, string> = {
  SMC_ICT: 'SMC/ICT',
  ORDERFLOW: 'Orderflow',
  INDICATEURS: 'Indicateurs',
  EA_BOTS: 'EA/Bots',
  CHARTISME: 'Chartisme',
};

export type BillingAddressData = {
  fullname: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export enum SubscriptionPlan {
  // Change this in accordance to your product's plans
  journal = 'journal',
  copy = 'copy',
  combo = 'combo',
}

export enum SubscriptionInterval {
  day = 'day',
  month = 'month',
  week = 'week',
  year = 'year',
}

export interface SubscriptionPrice {
  price: number;
  interval: SubscriptionInterval;
}

export interface SubscriptionMetadata {
  clicksPerDay: number;
}

/// Offre reçue par mail (fin d'essai, retour), appliquée d'office à la
/// souscription.
export interface PromoOffer {
  code: string;
  percentOff: number;
  expiresAt: string;
}

export interface DbPaymentMethod {
  id: string;
  brand: string;
  displayBrand: string | null;
  expYear: number;
  expMonth: number;
  last4: string;
  isDefault: boolean;
}

export interface UserSubscription {
  id: string;
  startedOn: Date;
  currentPeriodStart: Date;
  expiresOn: Date;
  canceledAtPeriodEnd: boolean;
  isTrial: boolean;
  extraAccounts: number;
  plan: SubscriptionPlan;
  /// Qui facture. Un abonnement App Store ou Google Play se gère chez eux.
  provider: 'stripe' | 'apple' | 'google';
  /// Accès à vie : ni échéance ni prélèvement. Absent d'un serveur pas
  /// encore à jour.
  lifetime?: boolean;
  /// Prélèvement du renouvellement refusé depuis cette date (Stripe). Absent
  /// d'un serveur pas encore à jour.
  paymentFailedAt?: string | null;
}

/// Ce que l'app iOS doit connaître avant un achat intégré.
export interface StoreConfig {
  /// À remettre tel quel au magasin, qui le recopie dans l'achat.
  appAccountToken: string;
  products: { productId: string; plan: SubscriptionPlan }[];
}

/// Réponse du serveur à un achat du magasin présenté par l'app.
export type StorePurchaseStatus =
  | 'recorded'
  | 'other-account'
  | 'already-subscribed'
  | 'inactive';

export type TrialStatus = 'eligible' | 'active' | 'expired' | 'not_applicable';

export interface TrialInfo {
  status: TrialStatus;
  endsAt: string | null;
  /** Computed by the backend: never derive it from the local clock. */
  daysRemaining: number | null;
  allowedAccounts: number;
}

export type InvoiceStatus = 'paid' | 'processing' | 'unpaid';

export interface InvoiceData {
  id: string;
  /** En unités (euros), pas en centimes. */
  amount: number;
  /** Absente des anciens serveurs : euro. */
  currency?: string;
  date: number;
  status?: InvoiceStatus | null;
  url: string | null;
}

export type TrackedAccountState = 'active' | 'almost-inactive' | 'inactive';

export const ACCOUNT_STATE_TEXT: { [key in TrackedAccountState]: string } = {
  active: 'Actif',
  inactive: 'Inactif',
  'almost-inactive': 'Presque inactif',
};

export const ACCOUNT_STATE_COLOR: { [key in TrackedAccountState]: string } = {
  active: 'bg-green-500',
  'almost-inactive': 'bg-[#E7D38A]',
  inactive: 'bg-red-500',
};

export interface AccountBaseStats {
  /// Commission moyenne payee par lot a l'ouverture, mesuree sur l'historique.
  commissionPerLot: number;
  beWins: number;
  beLosses: number;
  wins: number;
  losses: number;
  totalDeposits: number;
  averageLoss: number;
  averageProfit: number;
  totalGain: number;
  tradingRevenue: number;
  netTransfers: number;
  initialTransfer: number;
}

export enum AccountFailedReason {
  invalid_broker = 'invalid_broker',
  invalid_server = 'invalid_server',
  invalid_login = 'invalid_login',
  timeout = 'timeout',
  invalid_credentials = 'invalid_credentials',
}

export enum AccountType {
  METATRADER = 'METATRADER',
  CCXT = 'CCXT',
  CTRADER = 'CTRADER',
  RITHMIC = 'RITHMIC',
}

export interface TrackedAccount {
  uid: string;
  accountType: AccountType;
  exchange?: string;
  spotOnly?: boolean;
  login?: string;
  broker?: string;
  leverage?: number;
  tradeAllowed?: boolean;
  eaTradeAllowed?: boolean;
  //tradeMode?: string;
  //stopOutMode?: string;
  //company?: string;
  currency?: string;
  //name?: string;
  server?: string;
  balance?: number;
  credit?: number;
  profit?: number;
  equity?: number;
  margin?: number;
  marginFree?: number;
  copySettings: AccountCopySettings | null;
  masterSettings: MasterAccountSettings | null;
  slaveAccounts?: number;
  masterPasswordProvided: boolean;
  ctidTraderAccountId?: string;
  ctraderEnvironment?: string;
  rithmicUser?: string;
  rithmicSystemName?: string;
  /// Point d'accès (gateway) que la session doit rejoindre. Absent sur les
  /// comptes liés avant l'ouverture des passerelles de production : ils
  /// retombent sur celle de leur système.
  rithmicGatewayUri?: string;
  rithmicFcmId?: string;
  rithmicIbId?: string;
  rithmicAccountId?: string;
  archived: boolean;
  /// Fermé par sa propfirm et archivé d'office. Absent d'un serveur pas encore
  /// à jour.
  terminatedAt?: string | null;
  connected: boolean;
  failedReason: AccountFailedReason | null;
  /// Première connexion en cours : ni archivage, ni changement d'identifiants,
  /// ni suppression. Absent d'un serveur pas encore à jour.
  firstConnection?: boolean;
  order: number;
  state: TrackedAccountState;
  stats: AccountBaseStats;
  activityStart?: string;
  activityEnd?: string;
  propfirmSlug?: string;
  /// Programme : `1step`, `2step`, `3step`, `instant`.
  propfirmPhase?: string;
  /// Étape : `phase1`, `phase2`, `phase3`, `funded`.
  propfirmChallengeSize?: string;
  /// Demande de liaison gratuite d'une propfirm partenaire ; `null` : aucune.
  /// Absent d'un serveur pas encore à jour.
  partnerClaim?: 'pending' | 'verified' | 'rejected' | null;
  /// Nature du compte d'après le broker ; `linked` quand il ne la dit pas.
  /// Absent d'un serveur pas encore à jour.
  level?: 'real' | 'propfirm' | 'linked' | 'demo';
}

export enum RiskType {
  fixed_lots = 'fixed_lots',
  balance_multiplier = 'balance_multiplier',
  batch_multiplier = 'batch_multiplier',
  fixed_percent = 'fixed_percent',
}

export enum ProfitPeriod {
  sessions = 'sessions',
  ampm = 'ampm',
  daily = 'daily',
  weekly = 'weekly',
  monthly = 'monthly',
}

export const PROFIT_PERIOD_LABEL: { [key in ProfitPeriod]: string } = {
  [ProfitPeriod.sessions]: 'Sessions',
  [ProfitPeriod.ampm]: 'AM-PM',
  [ProfitPeriod.daily]: 'Daily',
  [ProfitPeriod.weekly]: 'Weekly',
  [ProfitPeriod.monthly]: 'Monthly',
};

export enum BettingSystem {
  martingale = 'martingale',
}

export enum ENewsRestriction {
  takeAfterRestriction = 'takeAfterRestriction',
  doNothing = 'doNothing',
  ignoreTP = 'ignoreTP',
  ignoreSL = 'ignoreSL',
}

export enum PriorityRiskRestriction {
  plRatio = 'plRatio',
  maxLossProfit = 'maxLossProfit',
  maxRisk = 'maxRisk',
  martingale = 'martingale',
}

export enum MartingaleResetPeriod {
  streak = 'streak',
  daily = 'daily',
  weekly = 'weekly',
  sessions = 'sessions',
}

export interface AccountCopySettings {
  accountId: string;
  masterAccountSettingsId: string;

  paused: boolean;

  // risk params
  riskType: RiskType;
  riskMultiplier: number | null;

  // TP/SL
  copyStopLoss: boolean;
  copyTakeProfit: boolean;
  copyPendingOrders: boolean;

  // Propfirm Protection
  eNewsAllowed: boolean;
  eNewsWindows: number | null; // in minutes
  plRatio: boolean;
  trailing: boolean;
  tProfit: number | null;
  mLoss: number | null;
  maxSize: boolean;
  batches: number | null;
  hedgingAllowed: boolean;
  minTradDayFarmer: boolean;
  minOpenTime: number | null; // in minutes

  // Psychology Management
  maxLoss: boolean;
  maxLossTr: number | null;
  maxLossPercent: number | null;
  maxLossPerPeriod: ProfitPeriod | null;
  maxProfit: boolean;
  maxProfitTr: number | null;
  maxProfitPercent: number | null;
  maxProfitPerPeriod: ProfitPeriod | null;
  maxRiskPerTrade: boolean;
  maxRiskValue: number | null;
  bettingSystem: BettingSystem | null;
  martingaleMultLoss: number | null;
  martingaleMultProfit: number | null;
  martingaleInitialRisk: number | null;
  martingaleResetPeriod: MartingaleResetPeriod | null;

  // complementary settings
  eNewsRestriction: ENewsRestriction | null;
  priorityRiskRestriction: PriorityRiskRestriction | null;
}

export enum CopyRestrictedPositionsType {
  eNews = 'eNews',
  pTargetDailyLoss = 'pTargetDailyLoss',
  everything = 'everything',
}

export interface MasterAccountSettings {
  paused: boolean;
  accountId: string;
  eNewsAllowed: boolean;
  eNewsWindows: number | null; // in minutes
  profitTDailyL: boolean;
  pTarget: number | null;
  dLoss: number | null;
  minTradDayFarmer: boolean;
  minOpenTime: number | null;
  maxRisk: number | null;
  rrMin: number | null;
  copyRestrictedPositions: CopyRestrictedPositionsType | null;
}

export enum TradeType {
  Buy = 'Buy',
  Sell = 'Sell',
  Balance = 'Balance',
}

export interface HistoricalData {
  id: string;
  accountId: string;
  posId: number;
  price: number;
  volume: number;
  time: number;
  entry: boolean;
  type: TradeType;
  symbol: string;
  profit: number;
  commission: number;
  reason: string;
  tp: number;
  sl: number;
  swap: number;
  maxDd: number;
  maxGain: number;
  createdAt: string;
  updatedAt: string;
}

export interface LiveData {
  id: string;
  accountId: string;
  posId: number;
  price: number;
  currentPrice: number;
  volume: number;
  symbol: string;
  time: number;
  type: number;
  posSl: number;
  posTp: number;
  maxDd: number;
  maxGain: number;
  posMagic: number;
  comment: string;
  currentPnl: number;
  createdAt: string;
  updatedAt: string;
}

export interface BankInfo {
  id: string;
  iban: string;
  fullname: string;
}

export interface PaginatedQueryResultDto<T> {
  results: T[];
  count: number;
  skip: number;
  take: number;
}
